import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { formOptions } from 'utils/validation/asset'
import {
  useGetAssetByCategoryAndIdQuery,
  useCreatePhysicalAssetMutation,
  usePatchPhysicalAssetMutation,
  useGetAssetTypesQuery,
  useCreateAssetStatusMutation,
} from 'services/physicalAssets'
import { CHURN_ZERO_EVENTS, ASSET_CATEGORY, DISPATCH_TICKET_STATUSES } from 'utils/constants'
import { trackEvent } from 'utils/tracking'
import { isLocationInYardList } from 'utils/helpers'
import { setSnackBar } from 'state/ui'
import WizardStepper from 'components/generic/stepper/WizardStepper'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import ContainerProperties from './ContainerProperties'

const ContainerAssetBlade = ({ onFormStateChange, onHideModal }) => {
  const { assetId } = useSelector(state => state.ui.formModalProps)
  const { locationId: yardLocationId, defaultLocationId: defaultYardLocationId } = useSelector(
    state => state.settings.details,
  )
  const { inOrgMode } = useSelector(state => state.ui)
  const { yardDetailsList } = useSelector(state => state.auth)

  const { enableMultiLocationYards } = useSelector(state => state.featureFlags?.variables || {})

  const relevantYardLocationId = useMemo(() => {
    return defaultYardLocationId && enableMultiLocationYards ? defaultYardLocationId : yardLocationId
  }, [defaultYardLocationId, yardLocationId])

  const assetCategory = ASSET_CATEGORY.CONTAINER
  const isUpdate = !!assetId
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const methods = useForm(formOptions)
  const {
    handleSubmit,
    setError,
    formState: { dirtyFields },
    reset,
  } = methods
  const isDirty = !!Object.keys(dirtyFields).length

  const [title, setTitle] = useState('New Container')

  const [createPhysicalAsset, { isLoading: isLoadingCreateAsset }] = useCreatePhysicalAssetMutation()
  const [patchPhysicalAsset, { isLoading: isLoadingPatchAsset }] = usePatchPhysicalAssetMutation()
  const [createAssetStatus, { isLoading: isLoadingCreateAssetStatus }] = useCreateAssetStatusMutation()
  const { data: assetData, isLoading: isLoadingAssetData } = useGetAssetByCategoryAndIdQuery(
    { assetCategory, assetId },
    {
      refetchOnMountOrArgChange: true,
      skip: !assetId,
    },
  )

  const canEditLocation = useMemo(() => {
    if (!assetData?.assetStatus) return true
    const { dispatchTicketId } = assetData.assetStatus
    const noDispatchTicketId = !dispatchTicketId
    const isOnCurrentTicket = [
      DISPATCH_TICKET_STATUSES.UNASSIGNED.id,
      DISPATCH_TICKET_STATUSES.ASSIGNED.id,
      DISPATCH_TICKET_STATUSES.IN_PROGRESS.id,
    ].includes(assetData?.dispatchTicketStatus)

    return noDispatchTicketId || !isOnCurrentTicket
  }, [assetData, relevantYardLocationId])

  const { data: assetTypesData, isLoading: isLoadingAssetTypesData } = useGetAssetTypesQuery({
    assetCategory: 'CONTAINER',
  })

  const isLoading =
    isLoadingAssetData ||
    isLoadingCreateAsset ||
    isLoadingPatchAsset ||
    isLoadingAssetTypesData ||
    isLoadingCreateAssetStatus

  useGlobalLoader(isLoading)

  const formatAsset = (data, isSubmit = false) => {
    const blank = isSubmit ? null : ''
    return {
      assetName: data?.assetName || blank,
      notes: data?.notes || blank,
      assetTypeId: data?.assetTypeId || blank,
      barcodeNumber: data?.barcodeNumber || blank,
      customer: null,
      ...(!isSubmit && {
        locationType: inOrgMode
          ? isLocationInYardList(data?.assetStatus?.currentLocation?.id, yardDetailsList)
            ? 'yard-location'
            : 'customer-location'
          : data?.assetStatus?.currentLocation?.id === relevantYardLocationId
            ? 'yard-location'
            : 'customer-location',
        locationId: data?.assetStatus?.currentLocation?.id,
      }),
    }
  }

  useEffect(() => {
    onFormStateChange(false)
  }, [])

  useEffect(() => {
    if (assetData) {
      setTitle(assetData?.assetName)
      const formattedMaterial = formatAsset(assetData)
      reset(formattedMaterial)
    }
  }, [assetData, relevantYardLocationId])

  const eventName = CHURN_ZERO_EVENTS[`${isUpdate ? 'PATCH' : 'CREATE'}_CONTAINER`]
  const handleSuccess = responseData => {
    trackEvent(eventName)
    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: `Container successfully ${isUpdate ? 'updated' : 'created'}`,
      }),
    )
    onHideModal()
    navigate(`/app/assets/${assetCategory.toLowerCase()}s/${responseData?.id || ''}`)
  }
  const handleError = error => {
    const errorMsg = error?.data?.error || `There was an error ${isUpdate ? 'updating' : 'creating'} the asset`
    if (errorMsg === 'Asset name already exists for organization') {
      setError('assetName', { message: errorMsg })
    }
    trackEvent({ eventName, errorMsg })
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: errorMsg,
      }),
    )
  }
  const onSubmit = async data => {
    const physicalAsset = formatAsset(data, true)
    const submitAPI = isUpdate ? patchPhysicalAsset : createPhysicalAsset
    const apiReqBody = isUpdate ? { body: physicalAsset, assetId, assetCategory } : { physicalAsset, assetCategory }

    await submitAPI(apiReqBody)
      .unwrap()
      .then(async responseData => {
        const assetHasLocation = !assetData?.assetStatus?.toLocationId
        const assetStayedAtYardLocation = inOrgMode
          ? data?.locationType === 'yard-location' &&
            isLocationInYardList(assetData?.assetStatus?.toLocationId, yardDetailsList)
          : data?.locationType === 'yard-location' && assetData?.assetStatus?.toLocationId === relevantYardLocationId
        const assetStayedAtCustomerLocation =
          data?.locationType === 'customer-location' && assetData?.assetStatus?.toLocationId === data?.locationId
        if (
          canEditLocation &&
          (!assetHasLocation || (assetHasLocation && !assetStayedAtYardLocation && !assetStayedAtCustomerLocation))
        ) {
          await createAssetStatus({
            body: {
              assetId: responseData?.id,
              toLocationId: data?.locationId,
              effectiveDate: data.assetStatus?.effectiveDate,
            },
            assetId: responseData?.id,
            assetCategory,
          })
            .unwrap()
            .then(() => handleSuccess(responseData))
            .catch(handleError)
        } else {
          handleSuccess(responseData)
        }
      })
      .catch(handleError)
  }

  const onValidatePartial = () => !isDirty

  const steps = [
    {
      label: 'Container Properties',
      showStepper: false,
      stepOptional: false,
      footerButtonTitles: ['SAVE', 'CANCEL'],
      contentComponent: (
        <ContainerProperties
          methods={methods}
          assetData={assetData}
          assetTypesData={assetTypesData?.Items}
          canEditLocation={canEditLocation}
        />
      ),
    },
  ]

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* todo add loading state */}

      <WizardStepper
        steps={steps}
        onHideModal={onHideModal}
        title={title}
        validationCheck
        nonLinear={isUpdate}
        disableAlertModal={!isDirty}
        onSubmitPartial={handleSubmit(onSubmit)}
        onValidatePartial={onValidatePartial}
      />
    </form>
  )
}

ContainerAssetBlade.propTypes = {
  onHideModal: PropTypes.func,
  onFormStateChange: PropTypes.func,
}

ContainerAssetBlade.defaultProps = {
  onHideModal: null,
  onFormStateChange: null,
}

export default ContainerAssetBlade
